import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useProcessing, {
    ProcessingEnvironment,
    Processor,
} from '@/src/Processing/components/hooks/useProcessing';
import useProcessingPresets from '@/src/Processing/components/hooks/useProcessingPresets';
import { useEventContext } from '@/src/v2/components/EventContext';
import EventPage from '../../chrome/EventPage';
import AssetPicker from '../../components/AssetPicker';
import {
    ConfirmDialog,
    Crumb,
    Dialog,
    Field,
    SaveBar,
    SectionCard,
    Select,
    TextInput,
    Toggle,
    ToggleRow,
} from '../../components/forms';
import { PageHeadActions } from '../../components/nav';
import { useToast } from '../../components/roster';
import { Segmented, TCard, TEmpty, TSkeleton } from '../../components/table';
import '../../pages-event.css';
import './processing.css';

/*
 * Flow editor — numbered steps with typed argument editors, a rail with
 * flow settings, the processor catalog, presets and a test run.
 * Same create/update/test/preset requests as the v2 editor.
 */

const HIDDEN_ARGS = new Set(['in', 'out']);

const stepTitle = (s: Processor) => s.title || s.name;

function fileName(url: string) {
    try {
        const clean = url.split('?')[0];
        return decodeURIComponent(clean.substring(clean.lastIndexOf('/') + 1));
    } catch {
        return url;
    }
}

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

interface Snapshot {
    steps: Processor[];
    environmentId: number | null;
    enabled: boolean;
}

/* ---- icons ---- */
const Ic = ({ d }: { d: string }) => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d={d} />
    </svg>
);
const UP = 'M12 19V5m0 0-6 6m6-6 6 6';
const DOWN = 'M12 5v14m0 0 6-6m-6 6-6-6';
const CLOSE = 'M6 6l12 12M18 6 6 18';

/* ---- argument editor ---- */
function ArgEditor({
    stepIndex,
    name,
    type,
    value,
    disabled,
    onChange,
    onPick,
}: {
    stepIndex: number;
    name: string;
    type: string | string[] | undefined;
    value: string | number | undefined;
    disabled: boolean;
    onChange: (v: string | number) => void;
    onPick: () => void;
}) {
    if (type === 'fileurl') {
        const v = String(value ?? '');
        const labelId = `pr-arg-${stepIndex}-${name}`;
        return (
            <div className="field" role="group" aria-labelledby={labelId}>
                <span className="field-l" id={labelId} style={{ textTransform: 'capitalize' }}>
                    {name}
                </span>
                <div className="pr-file">
                    <span className="th">{v && /^https?:/.test(v) && <img src={v} alt="" />}</span>
                    <span className="im">
                        <span className="fn" title={v}>
                            {v ? fileName(v) : 'No file chosen'}
                        </span>
                        <button type="button" className="tb-btn" disabled={disabled} onClick={onPick}>
                            {v ? 'Replace' : 'Choose'}
                        </button>
                    </span>
                </div>
            </div>
        );
    }
    if (Array.isArray(type)) {
        const current = String(value ?? '');
        const opts = type.includes(current) ? type : [...type, current];
        return (
            <Field label={name}>
                <Select value={current} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
                    {opts.map((o) => (
                        <option key={o} value={o}>
                            {o === '' ? '—' : o}
                        </option>
                    ))}
                </Select>
            </Field>
        );
    }
    const numeric = typeof value === 'number';
    return (
        <Field label={name}>
            <TextInput
                className="input ev-mono"
                inputMode={numeric ? 'decimal' : undefined}
                value={String(value ?? '')}
                disabled={disabled}
                onChange={(e) => {
                    const raw = e.target.value;
                    /* keep numbers as numbers when they still parse */
                    onChange(numeric && raw.trim() !== '' && Number.isFinite(+raw) ? +raw : raw);
                }}
            />
        </Field>
    );
}

/* ---- page ---- */
function FlowEditorBody() {
    const navigate = useNavigate();
    const { flowId } = useParams();
    const isNew = flowId == null;
    const { activationId: id, galleryId, resolved } = useEventContext();
    const {
        getProcessingFlow,
        processors: catalog,
        getProcessingEnvironments,
        createProcessingFlow,
        updateProcessingFlow,
        testFlow,
    } = useProcessing();
    const { presets, isLoading: presetsLoading, savePreset } = useProcessingPresets();
    const toast = useToast();

    const [steps, setSteps] = useState<Processor[]>([]);
    const [environmentId, setEnvironmentId] = useState<number | null>(null);
    const [enabled, setEnabled] = useState(true);
    const [envs, setEnvs] = useState<ProcessingEnvironment[]>([]);
    const [baseline, setBaseline] = useState<Snapshot | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<'failed' | 'missing' | null>(null);
    const [attempt, setAttempt] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [picker, setPicker] = useState<{ step: number; arg: string } | null>(null);
    const [confirmLeave, setConfirmLeave] = useState(false);
    const [presetOpen, setPresetOpen] = useState(false);
    const [presetName, setPresetName] = useState('');
    const [presetDesc, setPresetDesc] = useState('');
    const [presetSaving, setPresetSaving] = useState(false);
    const [presetError, setPresetError] = useState('');
    const [testBusy, setTestBusy] = useState(false);
    const [testResult, setTestResult] = useState<any>(null);
    const testFileRef = useRef<HTMLInputElement>(null);
    const savingRef = useRef(false);

    const listPath = `/activations/${id}/processing`;

    useEffect(() => {
        if (!resolved) return;
        if (galleryId == null) {
            setIsLoading(false);
            return;
        }
        let mounted = true;
        setIsLoading(true);
        setLoadError(null);
        (async () => {
            try {
                const envRows: ProcessingEnvironment[] = (await getProcessingEnvironments()) ?? [];
                if (!mounted) return;
                setEnvs(envRows);
                let init: Snapshot = { steps: [], environmentId: envRows[0]?.id ?? null, enabled: true };
                if (!isNew) {
                    const flow = await getProcessingFlow(+flowId!);
                    if (!mounted) return;
                    if (!flow) {
                        setLoadError('missing');
                        return;
                    }
                    let parsed: Processor[] = [];
                    try {
                        const raw = JSON.parse(flow.flowDefinition);
                        if (Array.isArray(raw)) parsed = raw;
                    } catch {
                        /* treat as empty */
                    }
                    init = { steps: parsed, environmentId: flow.environmentId, enabled: !!flow.enabled };
                }
                setSteps(clone(init.steps));
                setEnvironmentId(init.environmentId);
                setEnabled(init.enabled);
                setBaseline(init);
            } catch {
                if (mounted) setLoadError('failed');
            } finally {
                if (mounted) setIsLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolved, galleryId, flowId, attempt]);

    const dirtySections = useMemo(() => {
        if (!baseline) return [];
        const out: string[] = [];
        if (JSON.stringify(steps) !== JSON.stringify(baseline.steps)) out.push('Steps');
        if (environmentId !== baseline.environmentId || enabled !== baseline.enabled) out.push('Settings');
        return out;
    }, [steps, environmentId, enabled, baseline]);
    const dirty = dirtySections.length > 0;

    const envName = envs.find((e) => e.id === environmentId)?.environment ?? '';

    const filteredCatalog = useMemo(() => {
        const q = search.trim().toLowerCase();
        return catalog.filter(
            (p) =>
                !q ||
                p.name.toLowerCase().includes(q) ||
                (p.title ?? '').toLowerCase().includes(q) ||
                (p.description ?? '').toLowerCase().includes(q),
        );
    }, [catalog, search]);

    /* ---- step edits ---- */
    const edit = (fn: (rows: Processor[]) => Processor[]) => {
        setSteps(fn);
        setSaveError(null);
        setTestResult(null);
    };
    const setArg = (si: number, key: string, value: string | number) =>
        edit((rows) => rows.map((s, i) => (i === si ? { ...s, arguments: { ...s.arguments, [key]: value } } : s)));
    const move = (si: number, dir: -1 | 1) =>
        edit((rows) => {
            const to = si + dir;
            if (to < 0 || to >= rows.length) return rows;
            const next = [...rows];
            [next[si], next[to]] = [next[to], next[si]];
            return next;
        });
    const removeStep = (si: number) => {
        const removed = steps[si];
        edit((rows) => rows.filter((_, i) => i !== si));
        toast.show(`Removed ${stepTitle(removed)}.`, () =>
            setSteps((rows) => [...rows.slice(0, si), removed, ...rows.slice(si)]),
        );
    };
    const toggleSave = (si: number) =>
        edit((rows) => rows.map((r, i) => (i === si ? { ...r, saveOutput: !r.saveOutput } : r)));
    const addStep = (p: Processor) => {
        edit((rows) => [...rows, clone(p)]);
        toast.show(`Added ${stepTitle(p)} as step ${steps.length + 1}.`);
    };
    const applyPreset = (pr: any) => {
        if (!Array.isArray(pr.steps) || pr.steps.length === 0) return;
        edit((rows) => [...rows, ...clone(pr.steps)]);
        toast.show(`Added ${pr.steps.length} step${pr.steps.length === 1 ? '' : 's'} from ${pr.name}.`);
    };

    /* ---- save ---- */
    const save = async () => {
        if (savingRef.current || environmentId == null || galleryId == null) return;
        savingRef.current = true;
        setIsSaving(true);
        setSaveError(null);
        const def = JSON.stringify(steps);
        try {
            const ok = isNew
                ? await createProcessingFlow(galleryId, def, environmentId, enabled)
                : await updateProcessingFlow(+flowId!, def, environmentId, enabled);
            if (ok) {
                navigate(listPath);
                return;
            }
            setSaveError('Couldn’t save the flow — try again.');
        } catch {
            setSaveError('Couldn’t save the flow — try again.');
        } finally {
            savingRef.current = false;
            setIsSaving(false);
        }
    };

    const discard = () => {
        if (!baseline) return;
        setSteps(clone(baseline.steps));
        setEnvironmentId(baseline.environmentId);
        setEnabled(baseline.enabled);
        setSaveError(null);
    };

    const leave = () => (dirty ? setConfirmLeave(true) : navigate(listPath));

    /* ---- presets (org-wide) ---- */
    const submitPreset = async () => {
        const name = presetName.trim();
        if (!name || presetSaving) return;
        setPresetSaving(true);
        setPresetError('');
        const result = await savePreset(name, steps as any, presetDesc.trim() || undefined);
        setPresetSaving(false);
        if (result) {
            setPresetOpen(false);
            setPresetName('');
            setPresetDesc('');
            toast.show(`Preset “${name}” saved for your organization.`);
        } else {
            setPresetError('Couldn’t save the preset — try again.');
        }
    };

    /* ---- test run (nothing saved) ---- */
    const runTest = async (file: File) => {
        if (testBusy) return;
        setTestBusy(true);
        setTestResult(null);
        const result = await testFlow({
            // @ts-ignore FormData accepts File
            asset: file,
            flow: JSON.stringify(steps),
        }).catch(() => null);
        setTestResult(result ?? { error: 'Test failed' });
        setTestBusy(false);
    };
    const testUrl = (() => {
        if (!testResult) return null;
        const u = testResult.url ?? testResult.uri ?? testResult.outputUrl ?? testResult.output;
        return typeof u === 'string' && /^https?:/.test(u) ? u : null;
    })();
    const canTest = ['cloud', 'text'].includes(envName);

    /* ---- flow JSON ---- */
    const exportJson = () => {
        const blob = new Blob([JSON.stringify(steps, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'flow-definition.json';
        a.click();
        URL.revokeObjectURL(url);
    };
    const importJson = async (f: File) => {
        try {
            const parsed = JSON.parse(await f.text());
            if (Array.isArray(parsed)) {
                edit(() => parsed);
                toast.show(`Imported ${parsed.length} step${parsed.length === 1 ? '' : 's'}.`);
            } else {
                toast.show('That file isn’t a flow definition.');
            }
        } catch {
            toast.show('Couldn’t read that file.');
        }
    };

    /* ---- render ---- */
    const ready = resolved && !isLoading;
    const title = isNew ? 'New flow' : 'Edit flow';
    const sub = !ready
        ? 'Loading…'
        : `${steps.length} step${steps.length === 1 ? '' : 's'} · runs top to bottom on every new upload`;

    if (!ready || galleryId == null || loadError || !baseline) {
        return (
            <div className="ev-page pr-wide">
                <Crumb label="Processing" onClick={() => navigate(listPath)} />
                <PageHeadActions title={title} sub={ready ? undefined : sub} />
                <TCard>
                    {!ready ? (
                        <TSkeleton rows={4} />
                    ) : galleryId == null ? (
                        <TEmpty message="This event has no gallery yet — processing flows attach to the gallery." />
                    ) : loadError === 'missing' ? (
                        <TEmpty message="This flow doesn’t exist any more." actionLabel="Back to processing" onAction={() => navigate(listPath)} />
                    ) : (
                        <TEmpty message="Couldn’t load the flow." actionLabel="Retry" onAction={() => setAttempt((a) => a + 1)} />
                    )}
                </TCard>
            </div>
        );
    }

    return (
        <div className="ev-page pr-wide">
            <Crumb label="Processing" onClick={leave} />
            <PageHeadActions
                title={title}
                sub={sub}
                actions={
                    isNew ? (
                        <>
                            <button type="button" className="tb-btn" disabled={isSaving} onClick={leave}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="tb-new"
                                disabled={isSaving || environmentId == null}
                                onClick={save}
                            >
                                {isSaving ? 'Creating…' : 'Create flow'}
                            </button>
                        </>
                    ) : undefined
                }
            />
            {saveError && (
                <p className="field-e" role="alert" style={{ marginBottom: 12 }}>
                    {saveError}
                </p>
            )}

            <div className="pr-grid">
                {/* steps */}
                <div className="pr-main">
                    {steps.length === 0 && (
                        <TCard>
                            <TEmpty message="No steps — captures pass through untouched. Add a processor from the catalog." />
                        </TCard>
                    )}
                    {steps.map((s, si) => {
                        const unsupported = !!s.environments && !!envName && !s.environments.includes(envName);
                        const argKeys = Object.keys(s.arguments ?? {}).filter((k) => !HIDDEN_ARGS.has(k));
                        const t = stepTitle(s);
                        return (
                            <div className="sec" key={si}>
                                <div className="pr-step-h">
                                    <span className="pr-num" aria-hidden="true">
                                        {si + 1}
                                    </span>
                                    <div className="pr-step-t">
                                        <span className="nm">{t}</span>
                                        {s.title && s.title !== s.name && <span className="id">{s.name}</span>}
                                        {s.description && <div className="ds">{s.description}</div>}
                                        {unsupported && (
                                            <div className="pr-warn">Not supported in the {envName} environment</div>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className="pr-ic"
                                        aria-label={`Move step ${si + 1} up`}
                                        title="Move up"
                                        disabled={isSaving || si === 0}
                                        onClick={() => move(si, -1)}
                                    >
                                        <Ic d={UP} />
                                    </button>
                                    <button
                                        type="button"
                                        className="pr-ic"
                                        aria-label={`Move step ${si + 1} down`}
                                        title="Move down"
                                        disabled={isSaving || si === steps.length - 1}
                                        onClick={() => move(si, 1)}
                                    >
                                        <Ic d={DOWN} />
                                    </button>
                                    <button
                                        type="button"
                                        className="pr-ic del"
                                        aria-label={`Remove step ${si + 1}: ${t}`}
                                        title="Remove step"
                                        disabled={isSaving}
                                        onClick={() => removeStep(si)}
                                    >
                                        <Ic d={CLOSE} />
                                    </button>
                                </div>
                                {argKeys.length > 0 && (
                                    <div className="pr-args">
                                        {argKeys.map((key) => (
                                            <ArgEditor
                                                key={key}
                                                stepIndex={si}
                                                name={key}
                                                type={s.argumentTypes?.[key]}
                                                value={s.arguments[key]}
                                                disabled={isSaving}
                                                onChange={(v) => setArg(si, key, v)}
                                                onPick={() => setPicker({ step: si, arg: key })}
                                            />
                                        ))}
                                    </div>
                                )}
                                <div className="pr-save">
                                    <Toggle
                                        label="Save this step’s output to the gallery"
                                        checked={!!s.saveOutput}
                                        disabled={isSaving}
                                        onChange={() => toggleSave(si)}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* rail */}
                <div className="pr-rail">
                    <SectionCard title="Flow settings">
                        <div className="field">
                            <span className="field-l">Environment</span>
                            {envs.length === 0 ? (
                                <p className="quiet-empty">No environments available.</p>
                            ) : (
                                <Segmented<string>
                                    label="Environment"
                                    value={String(environmentId ?? '')}
                                    onChange={(v) => {
                                        setEnvironmentId(+v);
                                        setTestResult(null);
                                    }}
                                    options={envs.map((e) => ({ value: String(e.id), label: e.environment }))}
                                />
                            )}
                        </div>
                        <ToggleRow
                            label="Enabled"
                            sub="When on, every new upload to this event runs this flow."
                            checked={enabled}
                            disabled={isSaving}
                            onChange={setEnabled}
                        />
                    </SectionCard>

                    <SectionCard title="Add step" note={catalog.length ? `${catalog.length} processors` : undefined}>
                        <TextInput
                            type="search"
                            aria-label="Search processors"
                            placeholder="Search processors…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <div className="pr-cat">
                            {catalog.length === 0 ? (
                                <p className="pr-help">Loading processors…</p>
                            ) : filteredCatalog.length === 0 ? (
                                <p className="pr-help">No processors match.</p>
                            ) : (
                                filteredCatalog.map((p) => (
                                    <button
                                        type="button"
                                        key={p.name}
                                        className="pr-cat-i"
                                        disabled={isSaving}
                                        onClick={() => addStep(p)}
                                    >
                                        <span className="t">
                                            {stepTitle(p)}
                                            {p.environments && p.environments.length > 0 && (
                                                <span className="e">{p.environments.join(' · ')}</span>
                                            )}
                                        </span>
                                        {p.description && <span className="d">{p.description}</span>}
                                    </button>
                                ))
                            )}
                        </div>
                    </SectionCard>

                    <SectionCard title="Presets" note="shared across the organization">
                        {presetsLoading && presets.length === 0 ? (
                            <p className="pr-help">Loading presets…</p>
                        ) : presets.length === 0 ? (
                            <p className="quiet-empty">None saved yet.</p>
                        ) : (
                            <div className="pr-cat" style={{ marginTop: 0 }}>
                                {presets.map((pr) => (
                                    <button
                                        type="button"
                                        key={pr.id}
                                        className="pr-cat-i"
                                        disabled={isSaving}
                                        onClick={() => applyPreset(pr)}
                                    >
                                        <span className="t">
                                            {pr.name}
                                            <span className="e">{(pr.steps ?? []).length} steps</span>
                                        </span>
                                        {pr.description && <span className="d">{pr.description}</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                        {steps.length > 0 && (
                            <div className="pr-btns">
                                <button type="button" className="tb-btn" onClick={() => setPresetOpen(true)}>
                                    Save steps as preset…
                                </button>
                            </div>
                        )}
                    </SectionCard>

                    {canTest && (
                        <SectionCard title="Try it">
                            <p className="pr-help" style={{ marginTop: 0 }}>
                                Runs the current steps on a sample photo. Nothing is saved.
                            </p>
                            <div className="pr-btns">
                                <button
                                    type="button"
                                    className="tb-btn"
                                    disabled={testBusy || steps.length === 0}
                                    onClick={() => testFileRef.current?.click()}
                                >
                                    {testBusy ? 'Running…' : 'Test with a photo…'}
                                </button>
                            </div>
                            <input
                                ref={testFileRef}
                                type="file"
                                accept="image/*"
                                hidden
                                aria-label="Sample photo for test run"
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    e.target.value = '';
                                    if (f) runTest(f);
                                }}
                            />
                            {testResult && (
                                <div className="pr-result" role="status">
                                    {testUrl ? (
                                        <a href={testUrl} target="_blank" rel="noreferrer">
                                            <img src={testUrl} alt="Test result" />
                                            Open result
                                        </a>
                                    ) : testResult.error ? (
                                        <p className="field-e">{String(testResult.error)}</p>
                                    ) : (
                                        <pre>{JSON.stringify(testResult, null, 2)}</pre>
                                    )}
                                </div>
                            )}
                        </SectionCard>
                    )}

                    <SectionCard title="Flow JSON">
                        <div className="pr-btns" style={{ marginTop: 0 }}>
                            <button type="button" className="tb-btn" disabled={steps.length === 0} onClick={exportJson}>
                                Export
                            </button>
                            <label className="tb-btn" style={{ cursor: 'pointer' }}>
                                Import…
                                <input
                                    type="file"
                                    accept="application/json,.json"
                                    hidden
                                    disabled={isSaving}
                                    onChange={(e) => {
                                        const f = e.target.files?.[0];
                                        e.target.value = '';
                                        if (f) importJson(f);
                                    }}
                                />
                            </label>
                        </div>
                        <p className="pr-help">Import replaces the current steps until you save.</p>
                    </SectionCard>
                </div>
            </div>

            {!isNew && <SaveBar dirty={dirtySections} saving={isSaving} onSave={save} onDiscard={discard} />}

            {picker && (
                <AssetPicker
                    onClose={() => setPicker(null)}
                    onSelect={(url) => {
                        setArg(picker.step, picker.arg, url);
                        setPicker(null);
                    }}
                />
            )}

            <Dialog
                open={presetOpen}
                title="Save as preset"
                onClose={() => !presetSaving && setPresetOpen(false)}
                onSubmit={submitPreset}
                footer={
                    <>
                        <button
                            type="button"
                            className="tb-btn"
                            disabled={presetSaving}
                            onClick={() => setPresetOpen(false)}
                        >
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={presetSaving || !presetName.trim()}>
                            {presetSaving ? 'Saving…' : 'Save for organization'}
                        </button>
                    </>
                }
            >
                <p style={{ margin: '0 0 14px' }}>
                    Saves for everyone in the organization — the {steps.length} current step
                    {steps.length === 1 ? '' : 's'} become a preset any flow can add.
                </p>
                <Field label="Preset name" error={presetError || undefined}>
                    <TextInput
                        autoFocus
                        value={presetName}
                        disabled={presetSaving}
                        onChange={(e) => setPresetName(e.target.value)}
                    />
                </Field>
                <Field label="Description (optional)">
                    <TextInput
                        value={presetDesc}
                        disabled={presetSaving}
                        onChange={(e) => setPresetDesc(e.target.value)}
                    />
                </Field>
            </Dialog>

            <ConfirmDialog
                open={confirmLeave}
                title="Discard unsaved changes?"
                body="Your edits to this flow haven’t been saved."
                actionLabel="Discard"
                onConfirm={() => navigate(listPath)}
                onClose={() => setConfirmLeave(false)}
            />
            {toast.node}
        </div>
    );
}

export default function FlowEditorV3() {
    return (
        <EventPage section="capture">
            <FlowEditorBody />
        </EventPage>
    );
}
